import React, { Suspense, useEffect } from 'react';
import { Breadcrumb, Layout, Spin } from 'antd';
import { Link, Route, Switch, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import AppHeader from '../components/Header';
import { useTranslation } from 'react-i18next';
import { useLazyGetApiAuthMeQuery } from '../api/services/auth';
import { setLoading } from '../features/app/appSlice';
import { useDispatch, useSelector } from 'react-redux';
import { setUserInfo } from '../features/auth/authSlice';
import SideMenu from '../components/SideMenu';
import { RootState } from "../app/store";
import NotFoundPage from "../components/NotFoundPage";
import { ROUTE_LABELS, isGuidSegment } from "../data/breadcrumbLabels";

const Users = React.lazy(() => import('./Users/Users'));
const Profile = React.lazy(() => import('./Profile'));
const Dashboard = React.lazy(() => import('./Dashboard/Dashboard'));
const PeriodDefinition = React.lazy(() => import('./Periods/PeriodDefinition'));
const TargetAudienceSelection = React.lazy(() => import('./TargetAudience/TargetAudienceSelection'));
const ScaleSelection = React.lazy(() => import('./ScaleSelection/ScaleSelection'));
const PerformanceSystemPage = React.lazy(() => import('./PerformanceSystem/PerformanceSystemPage'));
const ParametersPage = React.lazy(() => import('./Parameters/ParametersPage'));
const SystemRoleManagement = React.lazy(() => import('./Roles/SystemRoleManagement'));
const UserRoleDefinitions = React.lazy(() => import('./Roles/UserRoleDefinitions'));
const OneOnOneMeetingSchedule = React.lazy(() => import('./OneOnOneMeetingSchedule/OneOnOneMeetingSchedule'));
const PerformanceCardScreen = React.lazy(() => import('./PerformanceCardScreen/PerformanceCardScreen'));
const StrategicOKREntryPage = React.lazy(() => import('./StrategicOKREntry/StrategicOKREntryPage'));
const DepartmentOKREntryPage = React.lazy(() => import('./DepartmentOKREntry/DepartmentOKREntryPage'));
const MyPerformanceCardScreen = React.lazy(() => import('./MyPerformanceCardsScreen/MyPerformanceCardsScreen'));
const MyFeedbacks = React.lazy(() => import("./Feedback/MyFeedbacks"));
const TeamFeedbacks = React.lazy(() => import("./Feedback/TeamFeedbacks"));
const ReceivedFeedbacks = React.lazy(() => import("./Feedback/ReceivedFeedbacks"));
const WithEmployees = React.lazy(() => import("./OneOnOnes/WithEmployees"));
const WithManager = React.lazy(() => import("./OneOnOnes/WithManager"));
const TeamOneOnOnes = React.lazy(() => import("./OneOnOnes/TeamOneOnOnes"));
const TeamsOKRs = React.lazy(() => import("./TeamsOKRs/TeamsOKRs"));
const StrategicOKRs = React.lazy(() => import("./StrategicOKRs/StrategicOKRs"));
const DepartmentOKRs = React.lazy(() => import("./DepartmentOKRs/DepartmentOKRs"));
const MyPerformanceResults = React.lazy(() => import("./MyPerformanceResults/MyPerformanceResults"));
const Reports = React.lazy(() => import("./Reports/Reports"));
const ManagerEvaluation = React.lazy(() => import("./ManagerEvaluation/ManagerEvaluation"));
const CalibrationEvaluation = React.lazy(() => import("./CalibrationEvaluation/CalibrationEvaluation"));
const MCompanyGroups = React.lazy(() => import("./SystemManagement/CompanyGroups/MCompanyGroups"));
const Companies = React.lazy(() => import("./SystemManagement/Companies/Companies"));
const LogViewer = React.lazy(() => import("./SystemManagement/Logs/LogViewer"));
const MCompanyDetails = React.lazy(() => import("./SystemManagement/CompanyDetails/MCompanyDetails"));
const ManagerEvaluationSettings = React.lazy(() => import("./Evaluations/ManagerEvaluationSettings"));
const NudgingEmails = React.lazy(() => import("./NudgingEmails/NudgingEmails"));
const DashboardConfigurator = React.lazy(() => import("./DashboardConfigurator/DashboardConfigurator"));
const CorporateGoalCardEntry = React.lazy(() => import("./CorporateGoalCardEntry/CorporateGoalCardEntry"));
const DepartmentGoalCardEntry = React.lazy(() => import("./DepartmentGoalCardEntry/DepartmentGoalCardEntry"));
const CompetencySetPage = React.lazy(() => import("./CompetencySet/CompetencySetPage"));
const KpiPoolPage = React.lazy(() => import("./KpiPool/KpiPoolPage"));
const RoleCompetencyPage = React.lazy(() => import("./RoleCompetencyPage/RoleCompetencyPage"));
const MyKPIPerformanceCardScreen = React.lazy(() => import("./MyKPIPerformanceCardsScreen/MyKPIPerformanceCardsScreen"));
const MyAssignedCompetencies = React.lazy(() => import("./MyAssignedCompetencies/MyAssignedCompetencies"));
const TeamCompetencies = React.lazy(() => import("./TeamCompetencies/TeamCompetencies"));
const TeamsKPIs = React.lazy(() => import("./TeamsKPIs/TeamsKPIs"));
const AdminPerformanceCards = React.lazy(() => import("./AdminPerformanceCards/AdminPerformanceCards"));
const AdminIndividualKpiCards = React.lazy(() => import("./AdminIndividualKpiCards/AdminIndividualKpiCards"));
const CorporateGoalResultEntry = React.lazy(() => import("./CorporateGoalResultEntry/CorporateGoalResultEntry"));
const DepartmentGoalResultEntry = React.lazy(() => import("./DepartmentGoalResultEntry/DepartmentGoalResultEntry"));

const { Sider, Content } = Layout;


const Home: React.FC = () => {
    const { t } = useTranslation();
    let { path, url } = useRouteMatch();
    const location = useLocation();
    const [getMyData, { data: userData, isLoading: myDataLoading }] = useLazyGetApiAuthMeQuery()
    const dispatch = useDispatch();
    const tenantInfo = useSelector((state: RootState) => state.persisted?.tenantInfo);
    const tenantLoadingState = useSelector((state: RootState) => state.persisted?.isTenantLoaded);
    const history = useHistory();

    useEffect(() => {
        if (tenantLoadingState && !tenantInfo) {
            history.push(`/404`);
        }
    }, [tenantInfo, tenantLoadingState]);

    useEffect(() => {
        dispatch(setLoading(myDataLoading))
    }, [myDataLoading])

    useEffect(() => {
        getMyData()
    }, [])

    useEffect(() => {
        if (userData) {
            dispatch(setUserInfo(userData))
        }
    }, [userData])

    const generateBreadcrumbs = () => {
        const pathSnippets = location.pathname.split('/').slice(2).filter((i) => i);

        // Eskiden her segmentin birikimli yolu (ör. "companydetails/16dac9.../
        // 58d3bb6f...") doğrudan i18n'e veriliyordu; çeviri bulunamadığında
        // ham GUID'ler ekranda görünüyordu. Artık yalnızca bilinen segmentler
        // (ROUTE_LABELS) etiket olarak gösteriliyor, GUID'ler ekranda hiç
        // basılmıyor — ama linkten çıkarılmıyor: bir etiketten hemen sonra
        // gelen GUID'ler (ör. companydetails/:groupId/:companyId) o rotanın
        // zorunlu parametreleridir; atlanırsa link 404'e düşer.
        const breadcrumbs: JSX.Element[] = [];
        pathSnippets.forEach((segment, index) => {
            if (isGuidSegment(segment) || !ROUTE_LABELS[segment]) return;

            let endIndex = index;
            while (endIndex + 1 < pathSnippets.length && isGuidSegment(pathSnippets[endIndex + 1])) {
                endIndex++;
            }

            const absolutePath = `/${tenantInfo?.id}/${pathSnippets.slice(0, endIndex + 1).join('/')}`;
            breadcrumbs.push(
                <Breadcrumb.Item key={absolutePath}>
                    <Link to={absolutePath}>{ROUTE_LABELS[segment]}</Link>
                </Breadcrumb.Item>
            );
        });

        return (
            <Breadcrumb style={{ margin: '16px 0' }}>
                <Breadcrumb.Item key="home"><Link to={`/${tenantInfo?.id}/`}>{t('home')}</Link></Breadcrumb.Item>
                {breadcrumbs}
            </Breadcrumb>
        );
    };

    const isDashboardHome = /^\/[^/]+\/?$/.test(location.pathname);

    return (
        <Layout style={{ height: '100vh' }}>
            <AppHeader />
            <Layout>
                <Sider width={250} style={{ margin: 10, borderRadius: 10, overflow: "hidden" }}>
                    <SideMenu />
                </Sider>
                <Layout style={{ padding: isDashboardHome ? '0' : '0 24px 24px' }}>
                    <Content style={{ margin: isDashboardHome ? '0' : '16px', display: 'flex', flexDirection: 'column' }}>
                        {!isDashboardHome && generateBreadcrumbs()}
                        <div style={{
                            padding: isDashboardHome ? 0 : 24,
                            background: isDashboardHome ? 'transparent' : '#fff',
                            borderRadius: isDashboardHome ? 0 : '16px',
                            flexGrow: 1,
                            overflow: 'auto'
                        }}>
                            <Suspense fallback={<div style={{display:'flex',justifyContent:'center',alignItems:'center',height:'100%'}}><Spin size="large" /></div>}>
                            <Switch>
                                <Route path={`/:id/users`} component={Users} />
                                <Route path={`/:id/profile`} component={Profile} />
                                <Route path={`/:id/perioddefinition`} component={PeriodDefinition} />
                                <Route path={`/:id/targetaudiencedefinition`} component={TargetAudienceSelection} />
                                <Route path={`/:id/scaledefinition`} component={ScaleSelection} />
                                <Route path={`/:id/performancesystemsdefinition`}
                                    component={PerformanceSystemPage} />
                                {/*<Route path={`/:id/corporategoalcardentry`} component={PerformanceGoalEntryPage}/>*/}
                                <Route path={`/:id/parameters`} component={ParametersPage} />
                                <Route path={`/:id/systemrole`} component={SystemRoleManagement} />
                                <Route path={`/:id/userrole`} component={UserRoleDefinitions} />
                                <Route path={`/:id/oneononemeetingschedule`} component={OneOnOneMeetingSchedule} />
                                <Route path={`/:id/performancecardscreation`} component={PerformanceCardScreen} />
                                <Route path={`/:id/strategicokrentry`} component={StrategicOKREntryPage} />
                                <Route path={`/:id/departmentokrentry`} component={DepartmentOKREntryPage} />

                                <Route path={`/:id/corporategoalcardentry`} component={CorporateGoalCardEntry} />
                                <Route path={`/:id/departmentgoalcardentry`} component={DepartmentGoalCardEntry} />

                                <Route path={`/:id/corporategoalresultentry`} component={CorporateGoalResultEntry} />
                                <Route path={`/:id/departmentgoalresultentry`} component={DepartmentGoalResultEntry} />

                                <Route path={`/:id/competencyentry`} component={CompetencySetPage} />
                                <Route path={`/:id/competencyroleentry`} component={RoleCompetencyPage} />

                                <Route path={`/:id/myperformancecardsstrategicokrs`}
                                    component={MyPerformanceCardScreen} />
                                <Route path={`/:id/myperformancecardskpis`}
                                    component={MyKPIPerformanceCardScreen} />
                                <Route path={`/:id/mycompetencies`}
                                    component={MyAssignedCompetencies} />
                                <Route path={`/:id/myteamscompetencies`}
                                    component={TeamCompetencies} />

                                <Route path={`/:id/adminperformancecardsokr`}
                                    component={AdminPerformanceCards} />
                                <Route path={`/:id/adminperformancecardskpi`}
                                    component={AdminPerformanceCards} />
                                <Route path={`/:id/adminindividualkpicards`}
                                    component={AdminIndividualKpiCards} />

                                <Route path={`/:id/dashboardmyteamsokrs`} component={TeamsOKRs} />
                                <Route path={`/:id/myteamskpis`} component={TeamsKPIs} />
                                <Route path={`/:id/dashboardstrategicokrs`} component={StrategicOKRs} />
                                <Route path={`/:id/myperformancecardsdepartmentokrs`} component={DepartmentOKRs} />

                                <Route path={`/:id/myfeedbacksoutgoing`} component={MyFeedbacks} />
                                <Route path={`/:id/myfeedbacksincoming`} component={ReceivedFeedbacks} />
                                <Route path={`/:id/myfeedbacksmyteams`} component={TeamFeedbacks} />

                                <Route path={`/:id/mydialogsteam`} component={WithEmployees} />
                                <Route path={`/:id/mydialogsmanager`} component={WithManager} />
                                <Route path={`/:id/mydialogsmyteams`} component={TeamOneOnOnes} />
                                <Route path={`/:id/kpipool`} component={KpiPoolPage} />

                                <Route path={`/:id/myperformanceresults`} component={MyPerformanceResults} />

                                <Route path={`/:id/managerevaluation`} component={ManagerEvaluation} />
                                <Route path={`/:id/calibrationevaluation`} component={CalibrationEvaluation} />
                                <Route path={`/:id/managerevaluationsettings`} component={ManagerEvaluationSettings} />
                                <Route path={`/:id/reports`} component={Reports} />

                                <Route path={`/:id/nudgingemails`} component={NudgingEmails} />

                                <Route path={`/:id/dashboardscreens`} component={DashboardConfigurator} />

                                <Route path={`/:id/logviewer`} component={LogViewer} />
                                <Route path={`/:id/company_groups`} component={MCompanyGroups} />
                                <Route path={`/:id/companies/:groupId`} component={Companies} />
                                <Route path={`/:id/companydetails/:groupId/:companyId`} component={MCompanyDetails} />

                                <Route exact path={`/:id`} component={Dashboard} />
                                <Route path="*" component={NotFoundPage} />
                            </Switch>
                            </Suspense>
                        </div>
                    </Content>
                </Layout>
            </Layout>
        </Layout>
    );
};

export default Home;
